import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../lib/api';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const STATUS_COLORS = {
  pending_payment: 'bg-yellow-100 text-yellow-700', paid: 'bg-blue-100 text-blue-700',
  shipped: 'bg-purple-100 text-purple-700', delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700', refunded: 'bg-gray-100 text-gray-600',
};
const STATUS_LABELS = {
  pending_payment: 'En attente', paid: 'Payée', shipped: 'Expédiée',
  delivered: 'Livrée', cancelled: 'Annulée', refunded: 'Remboursée',
};

const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');

  const load = useCallback(() => {
    const params = { page, limit: 20 };
    if (filter) params.status = filter;
    adminAPI.getOrders(params).then(r => { setOrders(r.data.orders); setTotal(r.data.total); }).catch(() => {});
  }, [page, filter]);
  useEffect(load, [load]);

  const updateStatus = async (orderId, status) => { await adminAPI.updateOrderStatus(orderId, status); load(); };

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-wrap gap-2 mb-4">
        {['', 'pending_payment', 'paid', 'shipped', 'delivered', 'cancelled'].map(s => (
          <button key={s} onClick={() => { setFilter(s); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === s ? 'bg-[#0C63BF] text-white' : 'bg-white border border-[#D6E8F0] text-[#5A8BA8] hover:bg-[#F0F7FA]'}`}>
            {s ? (STATUS_LABELS[s] || s) : 'Toutes'}
          </button>
        ))}
        <span className="ml-auto text-xs text-[#5A8BA8] self-center">{total} commande{total !== 1 ? 's' : ''}</span>
      </div>
      <div className="space-y-2">
        {orders.map(o => (
          <div key={o.id} className="bg-white rounded-xl border border-[#E8F4FD] p-3 sm:p-4" data-testid={`admin-order-${o.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#033871] truncate">{o.brand} {o.model}</p>
                <p className="text-[10px] text-[#5A8BA8]">#{o.id.slice(0, 8)} — {o.buyer_name} → {o.seller_name}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-[#0C63BF]">{o.total?.toFixed(2)}€</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[o.status] || 'bg-gray-100'}`}>
                  {STATUS_LABELS[o.status] || o.status}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-[10px]">
              <span className="text-[#5A8BA8]">Article: {o.item_price?.toFixed(2)}€</span>
              <span className="text-[#5A8BA8]">Livraison: {o.shipping_cost?.toFixed(2)}€</span>
              <span className="text-[#5A8BA8]">Protection: {o.protection_fee?.toFixed(2)}€</span>
              {o.tracking_number && <span className="text-[#0C63BF] font-medium">Tracking: {o.tracking_number}</span>}
              <div className="ml-auto flex gap-1">
                {o.status === 'paid' && <button onClick={() => updateStatus(o.id, 'shipped')} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-[10px] font-medium hover:bg-purple-200">Marquer expédiée</button>}
                {o.status === 'shipped' && <button onClick={() => updateStatus(o.id, 'delivered')} className="px-2 py-1 bg-green-100 text-green-700 rounded text-[10px] font-medium hover:bg-green-200">Marquer livrée</button>}
                {!['cancelled', 'refunded'].includes(o.status) && <button onClick={() => updateStatus(o.id, 'cancelled')} className="px-2 py-1 bg-red-100 text-red-700 rounded text-[10px] font-medium hover:bg-red-200">Annuler</button>}
              </div>
            </div>
          </div>
        ))}
        {orders.length === 0 && <p className="text-center text-sm text-[#5A8BA8] py-8">Aucune commande</p>}
      </div>
      {total > 20 && (
        <div className="flex justify-center items-center gap-3 mt-4">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg border disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-[#5A8BA8]">Page {page}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={orders.length < 20} className="p-1.5 rounded-lg border disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
};

export default OrdersTab;
